import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {Alert, Backdrop, Box, Checkbox, CircularProgress} from '@mui/material';
import dayjs from 'dayjs';
import {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppSelector} from '../../hooks/redux';
import {Company, CompanyListResponse} from '../../interfaces/Company';
import DataGrid, {DataGridColumn} from '../common/DataGrid';
import {CompanyActionsMenu} from './CompanyActionsMenu';
import {CompanySelectMenu} from './CompanySelectMenu';

interface Props {
  isAction: boolean;
}

export interface CompanyListRef {
  fetch?: Function;
}

export const CompanyList = forwardRef<CompanyListRef, PropsWithChildren<Props>>(
  ({isAction}: Props, ref) => {
    const currentCompanyId = useAppSelector(({app}) => app.companyId);
    /*******/
    /* ref */
    /*******/
    useImperativeHandle(ref, () => ({
      fetch: () => fetchData(),
    }));

    /*********/
    /* fetch */
    /*********/
    const [fetchedData, setFetchedData] = useState<CompanyListResponse>();
    const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
    const [fetchedInProgress, setFetchedInProgress] = useState(false);

    const fetchData = async () => {
      setFetchedInProgress(true);

      try {
        const resp = await API.get<CompanyListResponse>(
          `${apiBaseUrl}/company`
        );
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_1f332b7f');
        setFetchedErrors(messages);
      }

      setFetchedInProgress(false);
    };

    /*************/
    /* data grid */
    /*************/
    const rows = fetchedData?.items ?? [];
    const columns: DataGridColumn<Company>[] = [
      {field: 'id', headerName: 'ID', width: 60, resizeable: true},
      {field: 'name', width: 120, resizeable: true},
      {
        field: 'status',
        width: 80,
        renderCell: ({row}) => {
          return row.status === 'active' ? (
            <ToggleOnIcon color="success" />
          ) : (
            <ToggleOffIcon color="error" />
          );
        },
      },
      {field: 'mine_id', headerName: 'Mine ID', width: 120, resizeable: true},
      {
        field: 'added_date',
        width: 150,
        resizeable: true,
        valueFormatter: ({row}) => dayjs(row.added_date).format('MMM DD, YYYY'),
      },
      {
        field: 'selected',
        headerName: 'Selected',
        width: 150,
        resizeable: true,
        hidden: isAction,
        renderCell: ({row}) => {
          return <Checkbox checked={currentCompanyId === row.id} />;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,
        sxHeader: {textAlign: 'right'},
        sxCell: {textAlign: 'right'},
        renderCell: ({row}) => {
          if (isAction) {
            return (
              <CompanyActionsMenu
                item={row}
                onSubmitted={() => fetchData()}
                onDeleted={() => fetchData()}
              />
            );
          } else {
            return <CompanySelectMenu item={row} />;
          }
        },
      },
    ];

    useEffect(() => {
      fetchData();
    }, []);

    return (
      <Box position="relative">
        <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {fetchedErrors.map((error, index) => (
          <Alert key={index} severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        ))}

        <DataGrid
          rows={rows}
          columns={columns}
          loading={fetchedInProgress}
          size="small"
        />
      </Box>
    );
  }
);
